import { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

import { Button, FileInput } from '@core/components/ui';

/**
 * File Upload Zone Component
 *
 * Drag-and-drop file upload area with file input fallback.
 * Provides visual feedback during drag operations.
 *
 * @param {Object} props
 * @param {Function} props.onFileSelect - Callback when files are selected
 * @param {boolean} [props.disabled] - Whether upload is disabled
 * @param {boolean} [props.isDragging] - Whether files are being dragged over
 * @param {Object} props.dragHandlers - Drag event handlers (onDragOver, onDragLeave, onDrop)
 *
 * @example
 * <FileUploadZone
 *   onFileSelect={handleFileInput}
 *   disabled={uploadingFiles.size > 0}
 *   isDragging={isDragging}
 *   dragHandlers={dragHandlers}
 * />
 */
export function FileUploadZone({ onFileSelect, disabled, isDragging, dragHandlers }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
        isDragging
          ? 'border-brand bg-brand/5'
          : 'border-border-strong bg-transparent hover:border-border-strong'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      {...dragHandlers}
    >
      <FileInput
        ref={fileInputRef}
        multiple
        onChange={(files) => onFileSelect({ target: { files } })}
        id="file-upload-input"
        disabled={disabled}
      />

      <div className="flex flex-col items-center gap-3">
        {/* Upload Icon */}
        <div className={`rounded-full p-3 ${isDragging ? 'bg-brand/10' : 'bg-transparent'}`}>
          <Icon
            icon="lucide:upload-cloud"
            className={`size-8 ${isDragging ? 'text-brand' : 'text-subtle'}`}
          />
        </div>

        {/* Upload Button */}
        <Button
          variant="primary"
          size="md"
          onClick={handleButtonClick}
          disabled={disabled}
          type="button"
        >
          {t('file.uploadButton', 'Upload Files')}
        </Button>

        {/* Drag & Drop Text */}
        <p className="text-sm text-muted">
          {t('file.dragDrop', 'or drag and drop files here')}
        </p>
      </div>
    </div>
  );
}

FileUploadZone.propTypes = {
  onFileSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isDragging: PropTypes.bool,
  dragHandlers: PropTypes.shape({
    onDragOver: PropTypes.func.isRequired,
    onDragLeave: PropTypes.func.isRequired,
    onDrop: PropTypes.func.isRequired,
  }).isRequired,
};
